import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    Container, Box, Typography, Paper, Grid, Button, Avatar, Chip,
    CircularProgress, TextField, Dialog, DialogTitle, DialogContent,
    DialogActions, Tab, Tabs, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Tooltip, useTheme, useMediaQuery,
} from '@mui/material';
import {
    Group as GroupIcon, Add as AddIcon, ContentCopy as CopyIcon,
    EmojiEvents as TrophyIcon, ExitToApp as LeaveIcon, Bolt as BoltIcon,
    Speed as SpeedIcon, TrendingUp as TrendingIcon, MenuBook as BookIcon,
    Psychology as PsychIcon, Balance as BalanceIcon, LocalFireDepartment as FireIcon,
} from '@mui/icons-material';
import { flexGroupAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const TEMPLATES = [
    { value: 'speed_comprehension', label: 'Speed + Comprehension', desc: 'Highest Effective WPM wins', icon: BoltIcon, color: '#FFD700', days: 7 },
    { value: 'most_improved', label: 'Most Improved', desc: 'Biggest WPM gain over the period', icon: TrendingIcon, color: '#9C27B0', days: 14 },
    { value: 'consistency', label: '7-Day Consistency', desc: 'Read every day for 7 days', icon: FireIcon, color: '#FF9800', days: 7 },
    { value: 'perfect_comprehension', label: 'Perfect Comprehension', desc: '100% quiz scores', icon: PsychIcon, color: '#8DC63F', days: 7 },
    { value: 'volume', label: 'Reading Volume', desc: 'Most words read', icon: BookIcon, color: '#2196F3', days: 7 },
    { value: 'balanced', label: 'Balanced Reader', desc: 'Best combined speed + comprehension + volume', icon: BalanceIcon, color: '#00BFFF', days: 10 },
];

const FlexGroups = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const { user } = useAuth();

    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);
    const [createOpen, setCreateOpen] = useState(false);
    const [joinOpen, setJoinOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', description: '', visibility: 'private' });
    const [joinCode, setJoinCode] = useState('');
    const [error, setError] = useState('');
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [groupLeaderboard, setGroupLeaderboard] = useState(null);
    const [groupLoading, setGroupLoading] = useState(false);

    const fetchGroups = useCallback(async () => {
        try {
            setLoading(true);
            const data = await flexGroupAPI.getMyGroups();
            setGroups(data.groups || []);
        } catch (err) {
            console.error('Failed to fetch groups:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user) fetchGroups();
    }, [user, fetchGroups]);

    const handleCreate = async () => {
        if (!createForm.name.trim()) {
            setError('Group name is required.');
            return;
        }
        try {
            setError('');
            await flexGroupAPI.create(createForm);
            setCreateOpen(false);
            setCreateForm({ name: '', description: '', visibility: 'private' });
            fetchGroups();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create group.');
        }
    };

    const handleJoin = async () => {
        if (!joinCode.trim()) {
            setError('Invite code is required.');
            return;
        }
        try {
            setError('');
            await flexGroupAPI.join(joinCode.trim());
            setJoinOpen(false);
            setJoinCode('');
            fetchGroups();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to join group.');
        }
    };

    const handleSelectGroup = async (groupId) => {
        setTab(1);
        setGroupLoading(true);
        try {
            const [groupRes, lbRes] = await Promise.all([
                flexGroupAPI.getGroup(groupId),
                flexGroupAPI.getLeaderboard(groupId),
            ]);
            setSelectedGroup(groupRes.group);
            setGroupLeaderboard(lbRes.leaderboard);
        } catch (err) {
            console.error('Failed to load group:', err);
        } finally {
            setGroupLoading(false);
        }
    };

    const handleCreateChallenge = async (template) => {
        if (!selectedGroup) return;
        try {
            await flexGroupAPI.createChallenge(selectedGroup._id, template);
            const groupRes = await flexGroupAPI.getGroup(selectedGroup._id);
            setSelectedGroup(groupRes.group);
        } catch (err) {
            console.error('Failed to create challenge:', err);
        }
    };

    const handleLeave = async () => {
        if (!selectedGroup) return;
        try {
            await flexGroupAPI.leave(selectedGroup._id);
            setSelectedGroup(null);
            setGroupLeaderboard(null);
            setTab(0);
            fetchGroups();
        } catch (err) {
            console.error('Failed to leave group:', err);
        }
    };

    if (!user) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
                <GroupIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h5" gutterBottom>Join ReaderFLEX to use Group Challenges</Typography>
                <Button variant="contained" href="/register" sx={{ mt: 2 }}>Get Started</Button>
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>Group Challenges | ReaderFLEX</title>
                <meta name="description" content="Create private reading groups for workplaces, book clubs, and classrooms. Compete in group challenges with leaderboards." />
            </Helmet>

            <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <GroupIcon sx={{ color: '#00BFFF', fontSize: 32 }} />
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        Group Challenges
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Private reading groups for workplaces, book clubs, and classrooms. Compete with your group in structured challenges.
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                        Create Group
                    </Button>
                    <Button variant="outlined" startIcon={<GroupIcon />} onClick={() => setJoinOpen(true)}>
                        Join with Code
                    </Button>
                </Box>

                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
                    <Tab label="My Groups" />
                    {selectedGroup && <Tab label={selectedGroup.name} />}
                </Tabs>

                {/* Tab 0: Group List */}
                {tab === 0 && (
                    loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                    ) : groups.length === 0 ? (
                        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                You're not in any groups yet. Create one or join with an invite code.
                            </Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={2}>
                            {groups.map(g => (
                                <Grid item xs={12} sm={6} key={g._id}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`,
                                            cursor: 'pointer', transition: 'all 0.2s',
                                            '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)' },
                                        }}
                                        onClick={() => handleSelectGroup(g._id)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                            <GroupIcon color="primary" />
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{g.name}</Typography>
                                        </Box>
                                        {g.description && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {g.description}
                                            </Typography>
                                        )}
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            <Chip label={`${g.members?.length || 0} members`} size="small" variant="outlined" />
                                            <Chip label={g.visibility} size="small" variant="outlined" />
                                            {g.challenges?.length > 0 && (
                                                <Chip label={`${g.challenges.length} challenges`} size="small" color="primary" variant="outlined" />
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )
                )}

                {/* Tab 1: Group Detail */}
                {tab === 1 && selectedGroup && (
                    groupLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                    ) : (
                        <>
                            {/* Group Header */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 700 }}>{selectedGroup.name}</Typography>
                                        {selectedGroup.description && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {selectedGroup.description}
                                            </Typography>
                                        )}
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                            <Chip label={`${selectedGroup.members?.length} members`} size="small" />
                                            <Chip
                                                label={`Invite: ${selectedGroup.inviteCode}`}
                                                size="small"
                                                icon={<CopyIcon />}
                                                onClick={() => navigator.clipboard.writeText(selectedGroup.inviteCode)}
                                                clickable
                                            />
                                        </Box>
                                    </Box>
                                    <Button variant="outlined" color="error" size="small" startIcon={<LeaveIcon />} onClick={handleLeave}>
                                        Leave
                                    </Button>
                                </Box>
                            </Paper>

                            {/* Members */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Members</Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    {selectedGroup.members?.map(m => (
                                        <Box key={m.user?._id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar src={m.user?.profile?.avatarUrl} sx={{ width: 32, height: 32 }}>
                                                {m.user?.profile?.displayName?.[0]?.toUpperCase()}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    @{m.user?.profile?.displayName}
                                                </Typography>
                                                {m.role === 'admin' && <Chip label="Admin" size="small" color="primary" sx={{ height: 16, fontSize: '0.65rem' }} />}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>

                            {/* Group Leaderboard */}
                            {groupLeaderboard && groupLeaderboard.length > 0 && (
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                        <TrophyIcon sx={{ color: '#FFD700' }} />
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                            Group Leaderboard
                                        </Typography>
                                    </Box>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700 }}>Rank</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Member</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Eff. WPM</TableCell>
                                                    {!isMobile && <TableCell align="right" sx={{ fontWeight: 700 }}>Comp.</TableCell>}
                                                    {!isMobile && <TableCell align="right" sx={{ fontWeight: 700 }}>Streak</TableCell>}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {groupLeaderboard.map((row) => (
                                                    <TableRow
                                                        key={row._id}
                                                        hover
                                                        sx={{ cursor: 'pointer' }}
                                                        onClick={() => navigate(`/flex/${row.handle}`)}
                                                    >
                                                        <TableCell sx={{ fontWeight: 700 }}>
                                                            {row.rank <= 3 ? ['🥇', '🥈', '🥉'][row.rank - 1] : `#${row.rank}`}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Avatar src={row.avatarUrl} sx={{ width: 28, height: 28 }}>
                                                                    {row.handle[0]?.toUpperCase()}
                                                                </Avatar>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>@{row.handle}</Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700 }}>{row.effectiveWpm}</TableCell>
                                                        {!isMobile && <TableCell align="right">{row.comprehension}%</TableCell>}
                                                        {!isMobile && <TableCell align="right">{row.currentStreak}d</TableCell>}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            )}

                            {/* Active Challenges */}
                            {selectedGroup.challenges?.length > 0 && (
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Challenges</Typography>
                                    {selectedGroup.challenges.map(ch => {
                                        const tpl = TEMPLATES.find(t => t.value === ch.template);
                                        const Icon = tpl?.icon || BoltIcon;
                                        const isActive = ch.status === 'active' && new Date(ch.endDate) > new Date();
                                        return (
                                            <Box key={ch._id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider', mb: 1 }}>
                                                <Icon sx={{ color: tpl?.color }} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{ch.title}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {tpl?.desc} · Ends {new Date(ch.endDate).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={isActive ? 'Active' : ch.status}
                                                    size="small"
                                                    color={isActive ? 'success' : 'default'}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Paper>
                            )}

                            {/* Create Challenge */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Start a New Challenge</Typography>
                                <Grid container spacing={2}>
                                    {TEMPLATES.map(tpl => {
                                        const Icon = tpl.icon;
                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={tpl.value}>
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                                                        cursor: 'pointer', transition: 'all 0.2s',
                                                        '&:hover': { borderColor: tpl.color },
                                                    }}
                                                    onClick={() => handleCreateChallenge(tpl.value)}
                                                >
                                                    <Icon sx={{ color: tpl.color, mb: 1 }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{tpl.label}</Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        {tpl.desc}
                                                    </Typography>
                                                    <Chip label={`${tpl.days} days`} size="small" sx={{ mt: 1 }} />
                                                </Paper>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Paper>
                        </>
                    )
                )}

                {/* Create Dialog */}
                <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Create a Group</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Group Name"
                            value={createForm.name}
                            onChange={(e) => setCreateForm(p => ({ ...p, name: e.target.value }))}
                            sx={{ mb: 2, mt: 1 }}
                            inputProps={{ maxLength: 100 }}
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Description (optional)"
                            value={createForm.description}
                            onChange={(e) => setCreateForm(p => ({ ...p, description: e.target.value }))}
                            sx={{ mb: 2 }}
                            inputProps={{ maxLength: 500 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant={createForm.visibility === 'private' ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => setCreateForm(p => ({ ...p, visibility: 'private' }))}
                            >
                                Private
                            </Button>
                            <Button
                                variant={createForm.visibility === 'unlisted' ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => setCreateForm(p => ({ ...p, visibility: 'unlisted' }))}
                            >
                                Unlisted
                            </Button>
                        </Box>
                        {error && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleCreate}>Create</Button>
                    </DialogActions>
                </Dialog>

                {/* Join Dialog */}
                <Dialog open={joinOpen} onClose={() => setJoinOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Join a Group</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Invite Code"
                            value={joinCode}
                            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                            sx={{ mt: 1 }}
                            placeholder="ABCD1234"
                        />
                        {error && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setJoinOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleJoin}>Join</Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </>
    );
};

export default FlexGroups;
